import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../Modal/Modal";
import Icon from "../Icon/Icon";
import { useMe } from "../../hooks/useMe";
import { formatBytes } from "../../utils/format";

/**
 * Modali che possono aprirsi da qualunque pagina, comandati da eventi del
 * client HTTP (core/api.jsx):
 *
 *  - pr:permission-denied  403 (come PermissionDeniedModal di gestionale-gls):
 *    messaggio comprensibile e codice di riferimento del report automatico;
 *  - pr:piano              un limite del piano (dipendenti, esportazioni,
 *    spazio, file troppo grande): all'admin il pulsante per cambiare piano,
 *    agli altri chi deve farlo. Mai un vicolo cieco.
 */

const TITOLI = {
  PLAN_LIMIT_DIPENDENTI: "Limite di dipendenti raggiunto",
  PLAN_LIMIT_EXPORT: "Esportazioni sospese",
  STORAGE_QUOTA_EXCEEDED: "Spazio documenti esaurito",
  FILE_TOO_LARGE: "File troppo grande",
};

export default function GlobalModals() {
  const [denied, setDenied] = useState(null);
  const [piano, setPiano] = useState(null);
  const { isAdmin } = useMe();
  const navigate = useNavigate();

  useEffect(() => {
    const onDenied = (e) => setDenied(e.detail || {});
    const onPiano = (e) => setPiano(e.detail || {});
    window.addEventListener("pr:permission-denied", onDenied);
    window.addEventListener("pr:piano", onPiano);
    return () => {
      window.removeEventListener("pr:permission-denied", onDenied);
      window.removeEventListener("pr:piano", onPiano);
    };
  }, []);

  const cambiaPiano = piano && piano.error !== "FILE_TOO_LARGE";

  return (
    <>
      {denied && (
        <Modal
          title="Operazione non consentita"
          onClose={() => setDenied(null)}
          size="sm"
          footer={
            <button type="button" className="pr-btn pr-btn-confirm" onClick={() => setDenied(null)}>
              Ho capito
            </button>
          }
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            {denied.message && !/token|tenant|idAzienda/i.test(denied.message)
              ? denied.message
              : "Il tuo account non ha i permessi per questa operazione. Se pensi che sia un errore, chiedi a un amministratore."}
          </p>
          {denied.reference && (
            <p className="pr-hint" style={{ marginTop: 12 }}>
              Abbiamo già ricevuto una segnalazione. Codice di riferimento: <strong>{denied.reference}</strong>
            </p>
          )}
        </Modal>
      )}
      {piano && (
        <Modal
          title={TITOLI[piano.error] || "Limite del piano"}
          onClose={() => setPiano(null)}
          size="sm"
          footer={
            isAdmin && cambiaPiano ? (
              <>
                <button type="button" className="pr-btn pr-btn-cancel" onClick={() => setPiano(null)}>
                  Non ora
                </button>
                <button
                  type="button"
                  className="pr-btn pr-btn-confirm"
                  onClick={() => {
                    setPiano(null);
                    navigate("/GestioneAbbonamento");
                  }}
                >
                  <Icon name="card" size={18} /> Scegli un piano
                </button>
              </>
            ) : (
              <button type="button" className="pr-btn pr-btn-confirm" onClick={() => setPiano(null)}>
                Ho capito
              </button>
            )
          }
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>{piano.message}</p>
          {piano.error === "STORAGE_QUOTA_EXCEEDED" && piano.limitBytes && (
            <p className="pr-hint" style={{ marginTop: 10 }}>
              Usati {formatBytes(piano.usageBytes)} su {formatBytes(piano.limitBytes)}. Nessun file è stato perso: si bloccano solo i nuovi caricamenti.
            </p>
          )}
          {(piano.error === "PLAN_LIMIT_DIPENDENTI" || piano.error === "PLAN_LIMIT_EXPORT") && (
            <p className="pr-hint" style={{ marginTop: 10 }}>
              Presenze, link dei clienti e consultazione continuano a funzionare: nessun dato è bloccato.
            </p>
          )}
          {!isAdmin && cambiaPiano && (
            <p className="pr-hint" style={{ marginTop: 10 }}>
              Il piano lo cambia un amministratore dello studio: avvisalo che il limite è stato raggiunto.
            </p>
          )}
        </Modal>
      )}
    </>
  );
}
